import React, { useState } from "react";
import { Button, Sheet, Attention, Disclose, Typography, Link } from "@optimizely/axiom";
import { useBulkUpload } from "../../../hooks/useBulkUpload/useBulkUpload";
import { emitToast } from "../../../lib/toaster-utils";
import { UNEXPECTED_ERROR } from "../../../constants/constants";
import { UploadButton } from "../UploadButton/UploadButton";
import {
    ACCEPTED_EXTENSION,
    CSV_TEMPLATE_PATH,
    MAX_FILE_SIZE_MB,
    useCsvFileSelection
} from "../../../hooks/useCsvFileSelection/useCsvFileSelection";

interface CsvUploadSheetProps {
    isOpen: boolean;
    onClose: () => void;
    organizationId: string;
    onUpload?: (file: File) => Promise<void>;
}

export const CsvUploadSheet = ({ isOpen, onClose, organizationId, onUpload }: CsvUploadSheetProps) => {
    if (!isOpen) return null;
    return <CsvUploadSheetContent onClose={onClose} organizationId={organizationId} onUpload={onUpload} />;
};

CsvUploadSheet.displayName = "CsvUploadSheet";

const CsvUploadSheetContent = ({
    onClose,
    organizationId,
    onUpload
}: {
    onClose: () => void;
    organizationId: string;
    onUpload?: (file: File) => Promise<void>;
}) => {
    const { uploadUsersCsv } = useBulkUpload({ organizationId });
    const { selectedFile, errors, setErrors, uploadButtonKey, handleFileSelection, reset } = useCsvFileSelection();
    const [isUploading, setIsUploading] = useState(false);

    // The default path goes through `useBulkUpload` so a successful upload invalidates the
    // Uploads tab's cached list. A caller supplying `onUpload` owns its own cache sync.
    const uploadFile = onUpload ?? ((file: File) => uploadUsersCsv({ file }));

    const handleClose = () => {
        reset();
        setIsUploading(false);
        onClose();
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setErrors([]);

        try {
            await uploadFile(selectedFile);
            handleClose();
            emitToast({
                message: "Your file was uploaded successfully, and will be processed shortly."
            });
        } catch (e) {
            const message = e instanceof Error ? e.message : UNEXPECTED_ERROR;
            setErrors(message.split("\n"));
        } finally {
            setIsUploading(false);
        }
    };

    const hasErrors = errors.length > 0;

    return (
        <Sheet
            onClose={handleClose}
            title="Import Users with CSV"
            footerButtonList={[
                <Button key="cancel" onClick={handleClose} isDisabled={isUploading} data-testid="csv-upload-cancel">
                    Cancel
                </Button>,
                <Button
                    key="upload"
                    // eslint-disable-next-line react/style-prop-object
                    style="highlight"
                    onClick={handleUpload}
                    isDisabled={!selectedFile || isUploading}
                    isLoading={isUploading}
                    data-testid="csv-upload-submit"
                >
                    Upload
                </Button>
            ]}
        >
            {hasErrors && (
                <Attention type="bad-news" className="push--bottom" data-testid="csv-upload-error">
                    {errors.length === 1 ? (
                        errors[0]
                    ) : (
                        <Disclose title={`Upload failed with ${errors.length} errors:`}>
                            <ul className="push--left">
                                {errors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </Disclose>
                    )}
                </Attention>
            )}

            <Typography tag="p" type="body" className="push--bottom">
                Please use{" "}
                <Link href={CSV_TEMPLATE_PATH} download="users-template.csv">
                    this template
                </Link>{" "}
                to format your CSV file (max {MAX_FILE_SIZE_MB}MB and 1,000 users).
            </Typography>

            <UploadButton
                key={uploadButtonKey}
                onSelection={handleFileSelection}
                extensions={ACCEPTED_EXTENSION}
                disabled={isUploading}
                data-testid="csv-select-file"
            >
                Select a File
            </UploadButton>
        </Sheet>
    );
};
